/**
 * 自己维护的下拉选项(字典)。
 *
 * ★ 这里只定义**有哪些类目**,类目下面的具体选项是每家自己在后台加的
 *   —— 冻品、易碎、贵重、活鲜,每家的说法都不一样,写死一份等于
 *   逼着客户用我们的词,或者等我们发版。
 */

export const DICT_CATEGORIES = [
  {
    code: 'SPECIAL_ITEM',
    label: '特殊物品分类',
    /** ★ 说清楚这一类会影响什么 —— 否则运营不知道加了之后会发生什么 */
    effect: '建单时可以勾选;司机在订单上会看到高亮提醒,送达时要逐项确认交付',
  },
] as const;

export type DictCategory = (typeof DICT_CATEGORIES)[number]['code'];

export const DICT_CATEGORY_CODES: readonly string[] = DICT_CATEGORIES.map((c) => c.code);

export function isDictCategory(v: string): v is DictCategory {
  return DICT_CATEGORY_CODES.includes(v);
}

/**
 * 新建租户时给「特殊物品」预置的几项。
 *
 * ★ 预置不是写死:进的是数据表,客户可以改名、停用、加自己的。
 *   一个空列表会让人以为这个功能坏了 —— 而这几项恰好是
 *   本地配送里真正会出事的那几种(化冻、摔碎、丢了赔不起)。
 */
export const DEFAULT_SPECIAL_ITEMS = [
  { code: 'FROZEN', label: '冻品', icon: '🧊', sortOrder: 10 },
  { code: 'CHILLED', label: '冷藏', icon: '❄️', sortOrder: 20 },
  { code: 'FRAGILE', label: '易碎', icon: '🥚', sortOrder: 30 },
  { code: 'VALUABLE', label: '贵重', icon: '💎', sortOrder: 40 },
  { code: 'LIQUID', label: '液体', icon: '💧', sortOrder: 50 },
] as const;

/** 字典项在各端之间传递的形状 */
export interface DictItemDto {
  id: string;
  category: string;
  code: string;
  label: string;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
}

/** 订单上带出来的特殊物品 —— 已经翻译成人看得懂的样子 */
export interface SpecialItemDto {
  code: string;
  label: string;
  icon: string | null;
}

/**
 * 把 code 数组翻译成带名字的样子。
 *
 * ★ 认不出的 code **照样返回**(label 就用 code 本身),不能悄悄丢掉:
 *   分类被停用/改名之后,历史单上那一项还在货车上 ——
 *   界面上少显示一个「冻品」,司机就不会去开冷藏箱。
 */
export function decorateSpecialItems(
  codes: readonly string[],
  dict: readonly DictItemDto[],
): SpecialItemDto[] {
  const byCode = new Map(dict.filter((d) => d.category === 'SPECIAL_ITEM').map((d) => [d.code, d]));
  return codes.map((code) => {
    const hit = byCode.get(code);
    return { code, label: hit?.label ?? code, icon: hit?.icon ?? null };
  });
}
